import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import ReCAPTCHA from 'react-google-recaptcha'
import { Mail } from 'lucide-react'
import api from '@/services/api'
import { usePortal } from '@/contexts/PortalContext'
import { useRecaptchaSiteKey } from '@/hooks/useRecaptchaSiteKey'
import { onDark } from '@/utils/brandColor'

/**
 * Newsletter sign-up for white-label portals.
 *
 * The main site has had this on its home page all along; resellers had nothing. It posts to
 * the SAME `/newsletter/subscribe` endpoint, so a subscriber from any reseller's site lands
 * in our one newsletter table rather than somewhere separate — that is deliberate: the list
 * is Silux's, and it is what marketing sends from.
 *
 * Rendered as its OWN card above the footer's link columns. It first shipped as a bare
 * bordered strip appended after those columns, which put it under the Legal list and made it
 * read as a stray continuation of that column rather than a section of its own.
 *
 * Colours come from the portal but are not used raw: the footer is near-black, and a dark
 * brand colour (Lyra's purple) painted straight onto it produced a heading that was
 * effectively invisible. `onDark()` lifts the colour only as far as it needs to be read.
 */
export default function PortalNewsletterSignup() {
  const { portal } = usePortal()
  const primaryColor = portal?.primary_color || '#0ea5e9'
  const headingColor = onDark(primaryColor)
  const siteKey = useRecaptchaSiteKey()
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [token, setToken] = useState<string | null>(null)
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', mobile_number: '' })
  const [ok, setOk] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const subscribe = useMutation({
    mutationFn: async () => (await api.post('/newsletter/subscribe', { ...form, recaptcha_token: token })).data,
    onSuccess: () => {
      setOk('Thank you for signing up to our newsletter!')
      setErr(null)
      setForm({ first_name: '', last_name: '', email: '', mobile_number: '' })
      setToken(null)
      recaptchaRef.current?.reset()
    },
    onError: (e: any) => {
      setErr(e?.response?.data?.detail || 'Could not subscribe. Please try again.')
      setOk(null)
      setToken(null)
      recaptchaRef.current?.reset()
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.email) {
      setErr('Please enter your email address.')
      return
    }
    // Only demand the challenge when one is actually on screen. With no portal key
    // configured there is no widget to complete, and requiring a token would leave the
    // button permanently dead.
    if (siteKey && !token) {
      setErr('Please complete the reCAPTCHA.')
      return
    }
    subscribe.mutate()
  }

  const inputClass =
    'w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white ' +
    'placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/50'

  return (
    <section
      className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8 mb-12"
      aria-labelledby="portal-newsletter-heading"
    >
      <div className="grid md:grid-cols-2 gap-8 items-start">
        <div>
          <h3
            id="portal-newsletter-heading"
            className="text-xl sm:text-2xl font-bold mb-3 flex items-center gap-2 text-white"
          >
            <Mail className="w-6 h-6" style={{ color: headingColor }} />
            Subscribe to our newsletter
          </h3>
          <p className="text-sm text-gray-300 leading-relaxed">
            Deals, network news and product updates. Unsubscribe at any time.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input type="text" placeholder="First name" value={form.first_name}
                   onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                   className={inputClass} />
            <input type="text" placeholder="Last name" value={form.last_name}
                   onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                   className={inputClass} />
          </div>
          <input type="email" required placeholder="Email address" value={form.email}
                 onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                 className={inputClass} />
          <input type="tel" placeholder="Mobile number (optional)" value={form.mobile_number}
                 onChange={e => setForm(f => ({ ...f, mobile_number: e.target.value }))}
                 className={inputClass} />
          {siteKey && (
            <div className="scale-90 origin-left">
              <ReCAPTCHA ref={recaptchaRef} sitekey={siteKey} theme="dark" onChange={setToken} />
            </div>
          )}
          {ok && <p className="text-sm text-green-400">{ok}</p>}
          {err && <p className="text-sm text-red-400">{err}</p>}
          <button type="submit" disabled={subscribe.isPending}
                  className="px-5 py-2.5 rounded-lg font-semibold text-white disabled:opacity-50 hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: primaryColor }}>
            {subscribe.isPending ? 'Subscribing…' : 'Subscribe'}
          </button>
        </form>
      </div>
    </section>
  )
}
